import React from "react";
import Translations from "@translate/components/Member/Subscription/SubscriptionCard/PurchaseActions/ChannelPayButton";
import { cn } from "utils";
import { I18nUtil } from "@utils/i18n";
import { MemberProduct, PayChannel } from "@features/api/memberApi";
import {
  resolveChannelMeta,
  resolveChannelName,
} from "@utils/member/checkoutChannel";

const I18n = I18nUtil.createScoped(Translations);

/** 渠道按钮所在位置（埋点 `position`，fe-spec §3.6） */
export type CheckoutPosition = "member_pc" | "member_h5" | "onboarding_first";

interface ChannelPayButtonProps {
  channel: PayChannel;
  onClick: () => void;
  disabled?: boolean;
  /** 埋点：实际结算产品 id */
  productId?: string;
  /** 实际结算产品：未登记渠道的按钮文案取其 `payment_configs[].display_name` */
  product?: MemberProduct | null;
  position: CheckoutPosition;
  /** 本次下单携带的同意版本；终身产品不传 consent，这里也不传（埋点不虚报） */
  consentVersion?: string;
  /** 主按钮的渠道：传入时在「or」前标出主渠道名（只给第一个次级入口传，告诉用户主按钮走哪个渠道） */
  primaryChannel?: PayChannel | null;
}

/**
 * 次级渠道入口：「or + {渠道文案}」文字链（EDG-552，由 PayPalPayButton 通用化）。
 *
 * 显隐与顺序由父组件按 `pickCheckoutChannels(...).secondary` 决定；文案取 `resolveChannelMeta`
 * （已登记渠道用 `CHANNEL_META`，其余用后端 `display_name`），新渠道接入不改本组件。不可下单的渠道
 * （理论上已被 pickCheckoutChannels 过滤）渲染为空。
 *
 * 传 `primaryChannel` 时整行为「{主渠道名} · or {次级文案}」（如「Airwallex · or Pay with PayPal」），
 * 主渠道名是纯文本、不可点击。
 */
const ChannelPayButton: React.FC<ChannelPayButtonProps> = ({
  channel,
  onClick,
  disabled = false,
  productId,
  product,
  position,
  consentVersion,
  primaryChannel,
}) => {
  const meta = resolveChannelMeta(channel, product);
  if (!meta) {
    return null;
  }
  const channelLabel = meta.secondaryLabel();
  const primaryChannelName = primaryChannel
    ? resolveChannelName(primaryChannel, product)
    : undefined;
  const isOnboarding = position === "onboarding_first";
  return (
    <div
      data-or-view="checkout_channel_view"
      data-or-param-channel={channel}
      data-or-param-productid={productId}
      data-or-param-position={position}
      data-or-param-isprimary="0"
      className={cn(
        "flex flex-row items-center justify-center body_m text-content_tertiary",
        disabled && "text-content_tertiary/20",
      )}
    >
      {primaryChannelName && (
        <span className="mr-1">{`${primaryChannelName} ·`}</span>
      )}
      <span>{I18n.t("or")}</span>
      {/* label 写成字面量三元而非变量：埋点生成器按属性表达式文本收录，变量会被误解析成别处的同名常量 */}
      {/* disabled 渲染为纯文本（非交互）：声明式 data-or-event 不受 onClick 拦截影响，
          置灰态必须物理上不挂事件属性，否则点击量虚高（review #38） */}
      {disabled ? (
        <div
          data-openreplay-label={
            isOnboarding
              ? "onboarding_first_channel"
              : "member_purchaseactions_channel"
          }
          className="text-content_tertiary/20 button_m ml-1"
        >
          {channelLabel}
        </div>
      ) : (
        <button
          type="button"
          data-openreplay-label={
            isOnboarding
              ? "onboarding_first_channel"
              : "member_purchaseactions_channel"
          }
          data-or-event="checkout_channel_click"
          data-or-param-channel={channel}
          data-or-param-productid={productId}
          data-or-param-position={position}
          data-or-param-isprimary="0"
          data-or-param-consentversion={consentVersion}
          onClick={onClick}
          className="md:text-content_brand_primary text-content_accent4_primary hover:text-content_brand_primary/70 cursor-pointer button_m ml-1"
        >
          {channelLabel}
        </button>
      )}
    </div>
  );
};

export default ChannelPayButton;
